import type { CameraSpec, LayoutSimResult } from '../services/cameraLayoutService';
import {
  absoluteToCameraPanDisplayDeg,
  clampWallPoolEdgeOffsets,
  inferWallSide,
  poolBoundsFromPolygon,
  roundMeters1,
  wallCameraToPoolGapM,
  wallFaceHeadingDeg,
  wallOpticalCenterMeters,
  wallSideFromPlaceLabel,
  type PoolBounds,
  type WallPoolEdgeOffsetsM,
} from './cameraGridPlacement';

export type CameraLayoutReportInput = {
  poolId: number;
  /** 시설 표시명 (예: 미추홀시설). 없으면 ID로 대체 */
  poolName?: string | null;
  poolPolygon: number[][];
  poolLengthM: number;
  poolWidthM: number;
  laneCount: number;
  installHeightM: number;
  ceilingGridRows: number;
  ceilingGridCols: number;
  wallGridRows: number;
  wallGridCols: number;
  wallLeftCount?: number;
  wallRightCount?: number;
  wallPoolEdgeOffsetM: number;
  wallPoolEdgeOffsetsM?: WallPoolEdgeOffsetsM;
  wallMountOffsetM: number;
  defaultFocalLengthMm: number;
  defaultFovHDeg: number;
  defaultFovVDeg: number;
  resolutionW: number;
  resolutionH: number;
  cameras: CameraSpec[];
  result: LayoutSimResult | null;
  canvas: HTMLCanvasElement | null;
};

type WallSide = 'top' | 'bottom' | 'left' | 'right' | 'interior';

export type CameraInstallDetail = {
  index: number;
  name: string;
  mount_type: string;
  wall_side_label: string;
  /** 도면 정규 좌표 (0~100) */
  norm_x: number;
  norm_y: number;
  /** 도면 전체 스케일 기준 m (x→길이 L, y→폭 W) */
  map_x_m: number;
  map_y_m: number;
  /** 풀 사각형(도면 bbox) 안에서 가장자리·중심 기준 */
  along_pool_length_m: number;
  along_pool_width_m: number;
  from_pool_left_m: number;
  from_pool_top_m: number;
  optical_center_x_m: number;
  optical_center_y_m: number;
  pool_edge_offset_m: number;
  wall_mount_offset_m: number;
  height_m: number;
  spacing_to_next_m: number | null;
  spacing_note: string;
};

function stamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function round2(v: number) {
  return Math.round(v * 100) / 100;
}

/** 설치·간격 등 도면과 맞출 거리(m) — 소수 1자리 */
function roundDimM(v: number) {
  return roundMeters1(v);
}

function detectWallSide(
  xM: number,
  yM: number,
  poolLengthM: number,
  poolWidthM: number,
): WallSide {
  const edge = 0.22;
  const L = poolLengthM;
  const W = poolWidthM;
  const nearTop = yM <= W * edge;
  const nearBot = yM >= W * (1 - edge);
  const nearLeft = xM <= L * edge;
  const nearRight = xM >= L * (1 - edge);
  if (nearTop && !nearLeft && !nearRight) return 'top';
  if (nearBot && !nearLeft && !nearRight) return 'bottom';
  if (nearLeft && !nearTop && !nearBot) return 'left';
  if (nearRight && !nearTop && !nearBot) return 'right';
  if (nearTop) return 'top';
  if (nearBot) return 'bottom';
  if (nearLeft) return 'left';
  if (nearRight) return 'right';
  return 'interior';
}

function wallSideLabel(side: WallSide): string {
  switch (side) {
    case 'top':
      return '윗쪽 벽(길이 방향)';
    case 'bottom':
      return '아랫쪽 벽(길이 방향)';
    case 'left':
      return '왼쪽 벽(폭 방향)';
    case 'right':
      return '오른쪽 벽(폭 방향)';
    default:
      return '풀 내부/기타';
  }
}

function effectiveOpticalCenterM(
  cam: CameraSpec,
  mapXM: number,
  mapYM: number,
  poolLengthM: number,
  poolWidthM: number,
): { x: number; y: number } {
  if (cam.mount_type !== 'wall') {
    return { x: mapXM, y: mapYM };
  }
  const edge = Number(cam.pool_edge_offset_m) || 0;
  const wall = Number(cam.wall_mount_offset_m) || 0;
  const side = wallSideFromPlaceLabel(String(cam.name || ''));
  return wallOpticalCenterMeters(
    mapXM,
    mapYM,
    poolLengthM,
    poolWidthM,
    edge,
    wall,
    side,
  );
}

function poolSpanM(bounds: PoolBounds, poolLengthM: number, poolWidthM: number) {
  const spanNormX = Math.max(0.01, bounds.maxX - bounds.minX);
  const spanNormY = Math.max(0.01, bounds.maxY - bounds.minY);
  return {
    spanNormX,
    spanNormY,
    spanLengthM: poolLengthM,
    spanWidthM: poolWidthM,
  };
}

export function buildCameraInstallDetails(input: CameraLayoutReportInput): CameraInstallDetail[] {
  const bounds = poolBoundsFromPolygon(input.poolPolygon);
  const { spanNormX, spanNormY } = poolSpanM(bounds, input.poolLengthM, input.poolWidthM);
  const L = input.poolLengthM;
  const W = input.poolWidthM;

  const raw = input.cameras.map((cam, i) => {
    const mapXM = (Number(cam.x) / 100) * L;
    const mapYM = (Number(cam.y) / 100) * W;
    const side = cam.mount_type === 'wall' ? detectWallSide(mapXM, mapYM, L, W) : 'interior';
    const opt = effectiveOpticalCenterM(cam, mapXM, mapYM, L, W);
    const fromLeft = ((Number(cam.x) - bounds.minX) / spanNormX) * L;
    const fromTop = ((Number(cam.y) - bounds.minY) / spanNormY) * W;
    return {
      index: i + 1,
      name: cam.name || `CCTV ${i + 1}`,
      mount_type: cam.mount_type === 'wall' ? 'wall' : 'ceiling',
      wall_side_label: cam.mount_type === 'wall' ? wallSideLabel(side) : '천장',
      norm_x: roundDimM(Number(cam.x)),
      norm_y: roundDimM(Number(cam.y)),
      map_x_m: roundDimM(mapXM),
      map_y_m: roundDimM(mapYM),
      along_pool_length_m: roundDimM(fromLeft),
      along_pool_width_m: roundDimM(fromTop),
      from_pool_left_m: roundDimM(fromLeft),
      from_pool_top_m: roundDimM(fromTop),
      optical_center_x_m: roundDimM(opt.x),
      optical_center_y_m: roundDimM(opt.y),
      pool_edge_offset_m: roundDimM(Number(cam.pool_edge_offset_m) || input.wallPoolEdgeOffsetM),
      wall_mount_offset_m: roundDimM(Number(cam.wall_mount_offset_m) || input.wallMountOffsetM),
      height_m: roundDimM(Number(cam.height_m) || input.installHeightM),
      spacing_to_next_m: null as number | null,
      spacing_note: '',
      _side: side,
      _sortKey: side === 'left' || side === 'right' ? Number(cam.y) : Number(cam.x),
    };
  });

  const groups = new Map<string, typeof raw>();
  for (const row of raw) {
    const key = `${row.mount_type}:${row._side}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  }

  for (const [, list] of groups) {
    list.sort((a, b) => a._sortKey - b._sortKey);
    const horizontal = list[0]?._side === 'top' || list[0]?._side === 'bottom' || list[0]?.mount_type === 'ceiling';
    const spanNorm = horizontal ? spanNormX : spanNormY;
    const spanM = horizontal ? L : W;
    for (let j = 0; j < list.length; j++) {
      const cur = list[j];
      if (j < list.length - 1) {
        const nxt = list[j + 1];
        const d = (Math.abs(nxt._sortKey - cur._sortKey) / spanNorm) * spanM;
        cur.spacing_to_next_m = roundDimM(d);
        cur.spacing_note = `다음 CCTV까지 ${roundDimM(d)} m`;
      } else {
        const fromEdgeEnd = roundDimM(
          (((horizontal ? bounds.maxX : bounds.maxY) - cur._sortKey) / spanNorm) * spanM,
        );
        cur.spacing_note = `줄 끝까지 ${fromEdgeEnd} m`;
      }
    }
  }

  return raw.map(({ _side, _sortKey, ...rest }) => rest);
}

function reportPoolLabel(input: CameraLayoutReportInput): string {
  const name = (input.poolName || '').trim();
  return name || `시설 ${input.poolId}`;
}

function reportPoolFileSlug(input: CameraLayoutReportInput): string {
  const name = (input.poolName || '').trim().replace(/[\\/:*?"<>|]+/g, '_');
  if (name) return name;
  return `pool${input.poolId}`;
}

export function buildLayoutReportPayload(input: CameraLayoutReportInput) {
  const generatedAt = new Date().toISOString();
  const bounds = poolBoundsFromPolygon(input.poolPolygon);
  const span = poolSpanM(bounds, input.poolLengthM, input.poolWidthM);
  const installDetails = buildCameraInstallDetails(input);
  const laneWidthM =
    input.laneCount > 0 ? roundDimM(span.spanWidthM / input.laneCount) : null;
  const poolLabel = reportPoolLabel(input);

  return {
    kind: 'ansim_kyma_camera_layout_report',
    version: 2,
    generated_at: generatedAt,
    pool_id: input.poolId,
    pool_name: poolLabel,
    coordinate_legend: {
      description:
        '도면 풀 사각형=bbox. 가로 치수=입력 pool_length_m, 세로=pool_width_m. CCTV 간격=풀 가장자리(bbox) 기준 비율×실측.',
      pool_length_m: input.poolLengthM,
      pool_width_m: input.poolWidthM,
      pool_polygon_norm: input.poolPolygon,
      pool_bbox_norm: bounds,
      pool_drawn_length_m: roundDimM(span.spanLengthM),
      pool_drawn_width_m: roundDimM(span.spanWidthM),
      lane_count: input.laneCount,
      lane_width_m_estimate: laneWidthM,
    },
    pool: {
      id: input.poolId,
      name: poolLabel,
      length_m: input.poolLengthM,
      width_m: input.poolWidthM,
      lane_count: input.laneCount,
      install_height_m: input.installHeightM,
      area_m2_estimate: round2(input.poolLengthM * input.poolWidthM),
    },
    grids: {
      ceiling_rows: input.ceilingGridRows,
      ceiling_cols: input.ceilingGridCols,
      wall_rows: input.wallGridRows,
      wall_cols: input.wallGridCols,
      wall_left_count: input.wallLeftCount ?? 0,
      wall_right_count: input.wallRightCount ?? 0,
      wall_pool_edge_offset_m: input.wallPoolEdgeOffsetM,
      wall_mount_offset_m: input.wallMountOffsetM,
    },
    defaults: {
      focal_length_mm: input.defaultFocalLengthMm,
      fov_h_deg: input.defaultFovHDeg,
      fov_v_deg: input.defaultFovVDeg,
      resolution_w: input.resolutionW,
      resolution_h: input.resolutionH,
    },
    simulation: input.result
      ? {
          coverage_percent: input.result.coverage_percent,
          blind_percent: input.result.blind_percent,
          overlap_percent: input.result.overlap_percent,
          covered_area_m2: input.result.covered_area_m2,
          pool_area_m2: input.result.pool_area_m2,
          camera_count: input.result.camera_count,
          model: input.result.model,
        }
      : null,
    installation: installDetails,
    cameras: input.cameras.map((c, i) => ({
      index: i + 1,
      name: c.name,
      mount_type: c.mount_type,
      x: c.x,
      y: c.y,
      heading_deg: c.heading_deg,
      tilt_deg: c.tilt_deg,
      height_m: c.height_m,
      focal_length_mm: c.focal_length_mm,
      fov_h_deg: c.fov_h_deg,
      fov_v_deg: c.fov_v_deg,
      max_range_m: c.max_range_m,
      resolution_w: c.resolution_w,
      resolution_h: c.resolution_h,
      pool_edge_offset_m: c.pool_edge_offset_m,
      wall_mount_offset_m: c.wall_mount_offset_m,
      cctv_id: c.cctv_id,
      install: installDetails[i],
    })),
  };
}

export function downloadLayoutReportJson(input: CameraLayoutReportInput) {
  const payload = buildLayoutReportPayload(input);
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: 'application/json;charset=utf-8',
  });
  triggerDownload(blob, `camera_layout_${reportPoolFileSlug(input)}_${stamp()}.json`);
}

export function downloadLayoutMapPng(
  canvas: HTMLCanvasElement,
  poolId: number,
  poolName?: string | null,
) {
  canvas.toBlob((blob) => {
    if (!blob) return;
    const slug = reportPoolFileSlug({ poolId, poolName } as CameraLayoutReportInput);
    triggerDownload(blob, `camera_layout_map_${slug}_${stamp()}.png`);
  }, 'image/png');
}

function panDisplayDeg(
  c: CameraSpec,
  bounds: PoolBounds,
): { display: number; note: string } {
  if (c.mount_type === 'wall') {
    const side = inferWallSide(c, bounds);
    const disp = absoluteToCameraPanDisplayDeg(
      Number(c.heading_deg),
      wallFaceHeadingDeg(side),
    );
    return { display: disp, note: '0=정면' };
  }
  return { display: Number(c.heading_deg), note: '도면' };
}

function opticsTableRows(cameras: CameraSpec[], bounds: PoolBounds): string {
  return cameras
    .map((c, i) => {
      const mount = c.mount_type === 'wall' ? '벽' : '천장';
      const pan = panDisplayDeg(c, bounds);
      return `<tr>
        <td>${i + 1}</td>
        <td class="left">${escapeHtml(c.name || `CCTV ${i + 1}`)}</td>
        <td>${mount}</td>
        <td>${fmt1(Number(c.height_m))}</td>
        <td>${fmt1(Number(c.tilt_deg))}</td>
        <td>${fmt1(pan.display)}°<span style="font-size:10px;color:#666"> (${pan.note})</span></td>
        <td>${fmt2(Number(c.focal_length_mm))}</td>
        <td>${fmt1(Number(c.fov_h_deg))}</td>
        <td>${fmt1(Number(c.fov_v_deg))}</td>
        <td>${fmt1(Number(c.max_range_m))}</td>
      </tr>`;
    })
    .join('');
}

function fmt1(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return String(Math.round(Number(v) * 10) / 10);
}

function fmt2(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return String(Math.round(Number(v) * 100) / 100);
}

function installGuideRows(
  details: CameraInstallDetail[],
  cameras: CameraSpec[],
  bounds: PoolBounds,
): string {
  return details
    .map((d, i) => {
      const c = cameras[i];
      const mount = d.mount_type === 'wall' ? '벽면' : '천장';
      const where =
        d.mount_type === 'wall'
          ? `${escapeHtml(d.wall_side_label)} · 풀 왼쪽에서 ${d.from_pool_left_m} m · 윗변에서 ${d.from_pool_top_m} m`
          : `천장 · 풀 왼쪽에서 ${d.from_pool_left_m} m · 윗변에서 ${d.from_pool_top_m} m`;
      const spacing =
        d.spacing_to_next_m != null
          ? `다음 카메라까지 <strong>${d.spacing_to_next_m} m</strong>`
          : escapeHtml(d.spacing_note || '—');
      const wallGap =
        d.mount_type === 'wall'
          ? `풀↔카메라 간격 풀가장자리 ${d.pool_edge_offset_m} m + 벽장착 ${d.wall_mount_offset_m} m`
          : '직하(천장)';
      const pan = c ? panDisplayDeg(c, bounds) : { display: Number.NaN, note: '' };
      return `<tr>
        <td class="idx">${d.index}</td>
        <td class="left">
          <div class="cam-name">${escapeHtml(d.name)}</div>
          <div class="cam-sub">${mount} · ${escapeHtml(d.wall_side_label)}</div>
        </td>
        <td class="left">${where}</td>
        <td>${fmt1(d.height_m)} m</td>
        <td>${fmt1(Number(c?.tilt_deg))}°</td>
        <td>${fmt1(pan.display)}° (${pan.note})</td>
        <td>${fmt2(Number(c?.focal_length_mm))} mm</td>
        <td class="left">${spacing}<div class="cam-sub">${wallGap}</div></td>
      </tr>`;
    })
    .join('');
}

function kvRow(label: string, value: string): string {
  return `<div class="kv"><span class="k">${escapeHtml(label)}</span><span class="v">${value}</span></div>`;
}

export function printLayoutReport(input: CameraLayoutReportInput) {
  const png = input.canvas?.toDataURL('image/png') ?? '';
  const r = input.result;
  const covN = r ? Number(r.coverage_percent) : NaN;
  const blindN = r ? Number(r.blind_percent) : NaN;
  const overlapN = r ? Number(r.overlap_percent) : NaN;
  const cov = Number.isFinite(covN) ? `${fmt1(covN)}%` : '—';
  const blind = Number.isFinite(blindN) ? `${fmt1(blindN)}%` : '—';
  const overlap = Number.isFinite(overlapN) ? `${fmt1(overlapN)}%` : '—';
  const covered = r?.covered_area_m2 != null ? `${fmt1(Number(r.covered_area_m2))} m²` : '—';
  const poolArea = r?.pool_area_m2 != null ? `${fmt1(Number(r.pool_area_m2))} m²` : '—';
  const payload = buildLayoutReportPayload(input);
  const coord = payload.coordinate_legend;
  const install = payload.installation as CameraInstallDetail[];
  const poolBounds = poolBoundsFromPolygon(input.poolPolygon);
  const poolLabel = reportPoolLabel(input);
  const nCeil = input.cameras.filter((c) => c.mount_type !== 'wall').length;
  const nWall = input.cameras.filter((c) => c.mount_type === 'wall').length;
  const areaEst = payload.pool.area_m2_estimate;
  const nowLabel = new Date().toLocaleString('ko-KR');

  const covClass =
    Number.isFinite(covN) && covN >= 95 ? 'ok' : Number.isFinite(covN) && covN >= 85 ? 'mid' : 'bad';
  const blindClass =
    Number.isFinite(blindN) && blindN <= 5 ? 'ok' : Number.isFinite(blindN) && blindN <= 15 ? 'mid' : 'bad';

  const html = `<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="utf-8" />
  <title>카메라 배치 결과 — ${escapeHtml(poolLabel)}</title>
  <style>
    :root {
      --ink: #1a2332;
      --muted: #5c6b7a;
      --line: #d5dde5;
      --bg: #f4f7fa;
      --card: #ffffff;
      --accent: #0d6e6e;
      --ok: #1b7a3d;
      --mid: #b86e00;
      --bad: #b42318;
    }
    * { box-sizing: border-box; }
    body {
      font-family: "Malgun Gothic", "Apple SD Gothic Neo", sans-serif;
      margin: 0;
      padding: 18px 22px 28px;
      color: var(--ink);
      font-size: 12.5px;
      line-height: 1.45;
      background: #fff;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 16px;
      border-bottom: 3px solid var(--accent);
      padding-bottom: 12px;
      margin-bottom: 16px;
    }
    .header h1 {
      margin: 0 0 4px;
      font-size: 20px;
      letter-spacing: -0.02em;
    }
    .header .sub { color: var(--muted); font-size: 12px; }
    .badge {
      background: var(--accent);
      color: #fff;
      font-size: 11px;
      font-weight: 700;
      padding: 4px 10px;
      border-radius: 999px;
      white-space: nowrap;
    }
    h2 {
      font-size: 14px;
      margin: 20px 0 8px;
      padding-left: 8px;
      border-left: 4px solid var(--accent);
    }
    .kpi {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 10px;
      margin: 12px 0 4px;
    }
    .kpi .card {
      background: var(--bg);
      border: 1px solid var(--line);
      border-radius: 10px;
      padding: 10px 12px;
      text-align: center;
    }
    .kpi .label { font-size: 11px; color: var(--muted); margin-bottom: 4px; }
    .kpi .value { font-size: 22px; font-weight: 800; letter-spacing: -0.03em; }
    .kpi .hint { font-size: 10.5px; color: var(--muted); margin-top: 2px; }
    .kpi .ok .value { color: var(--ok); }
    .kpi .mid .value { color: var(--mid); }
    .kpi .bad .value { color: var(--bad); }
    .grid2 {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }
    .panel {
      background: var(--bg);
      border: 1px solid var(--line);
      border-radius: 10px;
      padding: 12px 14px;
    }
    .panel h3 {
      margin: 0 0 8px;
      font-size: 12.5px;
      color: var(--accent);
    }
    .kv { display: flex; justify-content: space-between; gap: 12px; padding: 3px 0; border-bottom: 1px dashed #e2e8ef; }
    .kv:last-child { border-bottom: 0; }
    .kv .k { color: var(--muted); flex: 0 0 42%; }
    .kv .v { font-weight: 600; text-align: right; flex: 1; }
    .map-wrap {
      margin: 10px 0 4px;
      border: 1px solid var(--line);
      border-radius: 10px;
      overflow: hidden;
      background: #fff;
    }
    .map-wrap img { display: block; width: 100%; height: auto; }
    .map-cap {
      padding: 8px 12px;
      background: var(--bg);
      color: var(--muted);
      font-size: 11px;
      border-top: 1px solid var(--line);
    }
    table {
      border-collapse: collapse;
      width: 100%;
      font-size: 11px;
      margin-top: 6px;
    }
    th, td {
      border: 1px solid var(--line);
      padding: 6px 7px;
      text-align: center;
      vertical-align: top;
    }
    th {
      background: #e8eef3;
      font-weight: 700;
      color: #334155;
    }
    td.left, th.left { text-align: left; }
    td.idx { font-weight: 800; color: var(--accent); width: 28px; }
    .cam-name { font-weight: 700; }
    .cam-sub { color: var(--muted); font-size: 10.5px; margin-top: 2px; }
    .legend {
      margin-top: 10px;
      padding: 10px 12px;
      border-radius: 8px;
      background: #fff8e8;
      border: 1px solid #f0d9a0;
      font-size: 11px;
      color: #5c4a1a;
    }
    .footer {
      margin-top: 18px;
      padding-top: 8px;
      border-top: 1px solid var(--line);
      color: var(--muted);
      font-size: 10.5px;
      display: flex;
      justify-content: space-between;
      gap: 12px;
    }
    @media print {
      body { padding: 8px 10px; }
      .kpi .card, .panel, .map-wrap { break-inside: avoid; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; }
      h2 { page-break-after: avoid; }
    }
    @media screen and (max-width: 900px) {
      .kpi { grid-template-columns: repeat(2, 1fr); }
      .grid2 { grid-template-columns: 1fr; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <h1>카메라 배치 · 설치 결과</h1>
      <div class="sub">${escapeHtml(poolLabel)} · ${escapeHtml(nowLabel)}</div>
      <div class="sub">자동 계산값(높이·각도·초점·간격·커버리지) 요약 · 현장 시공 참고용</div>
    </div>
    <div class="badge">PDF / 인쇄</div>
  </div>

  <h2>1. 한눈에 보는 결과</h2>
  <div class="kpi">
    <div class="card ${covClass}">
      <div class="label">수면 커버리지</div>
      <div class="value">${cov}</div>
      <div class="hint">${covered} / ${poolArea}</div>
    </div>
    <div class="card ${blindClass}">
      <div class="label">사각(미커버)</div>
      <div class="value">${blind}</div>
      <div class="hint">낮을수록 좋음</div>
    </div>
    <div class="card">
      <div class="label">겹침</div>
      <div class="value">${overlap}</div>
      <div class="hint">중복 감시 비율</div>
    </div>
    <div class="card">
      <div class="label">카메라</div>
      <div class="value">${input.cameras.length}<span style="font-size:14px;font-weight:600">대</span></div>
      <div class="hint">천장 ${nCeil} · 벽 ${nWall}</div>
    </div>
  </div>

  <h2>2. 자동 계산 · 입력 요약</h2>
  <div class="grid2">
    <div class="panel">
      <h3>수영장 치수</h3>
      ${kvRow('길이 L (도면 가로)', `${input.poolLengthM} m`)}
      ${kvRow('폭 W (도면 세로)', `${input.poolWidthM} m`)}
      ${kvRow('면적 (L×W)', `약 ${areaEst} m²`)}
      ${kvRow('레인 수 / 레인 폭', `${input.laneCount}개 / 약 ${coord.lane_width_m_estimate ?? '—'} m`)}
    </div>
    <div class="panel">
      <h3>설치·광학 (공통)</h3>
      ${kvRow('설치 높이 H', `${input.installHeightM} m`)}
      ${kvRow('초점 (기본)', `${input.defaultFocalLengthMm} mm`)}
      ${kvRow('화각 H / V', `${fmt1(input.defaultFovHDeg)}° / ${fmt1(input.defaultFovVDeg)}°`)}
      ${kvRow('해상도', `${input.resolutionW} × ${input.resolutionH}`)}
    </div>
    <div class="panel">
      <h3>배치 격자 (자동/수동)</h3>
      ${kvRow('천장 격자', `${input.ceilingGridRows}행 × ${input.ceilingGridCols}열`)}
      ${kvRow('벽(장변) 격자', `${input.wallGridRows}행 × ${input.wallGridCols}열`)}
      ${kvRow('좌벽 / 우벽', `${input.wallLeftCount ?? 0}대 / ${input.wallRightCount ?? 0}대`)}
      ${(() => {
        const e = clampWallPoolEdgeOffsets(
          input.wallPoolEdgeOffsetsM,
          input.wallPoolEdgeOffsetM,
        );
        return kvRow(
          '벽·풀 간격 (상/하/좌/우)',
          `${e.top} / ${e.bottom} / ${e.left} / ${e.right} m · 장착 ${input.wallMountOffsetM} m`,
        );
      })()}
    </div>
    <div class="panel">
      <h3>읽는 법 (짧게)</h3>
      ${kvRow('풀 왼쪽에서 ○m', '도면 왼쪽(짧은 변) 기준 거리')}
      ${kvRow('윗변에서 ○m', '도면 위쪽(긴 변) 기준 거리')}
      ${kvRow('하향각', '천장 90°=직하 · 벽은 보통 18~30°(가장자리 여유)')}
      ${kvRow('방위(팬·벽)', '카메라 기준. 0°=정면(수면 안쪽), +우측, −좌측. 도면 화살표와 동일')}
      ${kvRow('방위(팬·천장)', '도면 절대각. 0°=우측, 90°=아래, 180°=좌, 270°=위')}
    </div>
  </div>

  <h2>3. 배치 도면</h2>
  ${
    png
      ? `<div class="map-wrap">
          <img src="${png}" alt="배치 도면" />
          <div class="map-cap">노란 영역 ≈ 각 카메라 화각·커버리지. 숫자 = 카메라 번호. 인쇄 시 「용지에 맞춤」 권장.</div>
        </div>`
      : `<div class="panel">도면 이미지 없음. 시뮬레이터에서 배치 후 다시 출력.</div>`
  }

  <h2>4. 카메라별 설치 안내 (현장용)</h2>
  <table>
    <thead>
      <tr>
        <th>#</th>
        <th class="left">카메라</th>
        <th class="left">어디(위치)</th>
        <th>높이</th>
        <th>하향</th>
        <th>방위</th>
        <th>초점</th>
        <th class="left">간격 · 비고</th>
      </tr>
    </thead>
    <tbody>${installGuideRows(install, input.cameras, poolBounds)}</tbody>
  </table>

  <h2>5. 광학·시야 상세</h2>
  <table>
    <thead>
      <tr>
        <th>#</th><th class="left">이름</th><th>설치</th>
        <th>높이 m</th><th>하향°</th><th>방위°</th>
        <th>초점 mm</th><th>H-FOV°</th><th>V-FOV°</th><th>시야 m</th>
      </tr>
    </thead>
    <tbody>${opticsTableRows(input.cameras, poolBounds)}</tbody>
 </table>

 <div class="legend">
 <strong>참고</strong> —
 좌표는 도면 풀 사각형(bbox) 기준. 「다음 카메라까지」는 같은 벽·같은 천장 줄에서 이웃과의 거리.
 커버리지는 시뮬 격자 추정값이며, 현장 글레어·장애물은 별도 확인 필요.
 </div>

 <div class="footer">
 <span>Ansim-Kyma · 카메라 배치 리포트</span>
 <span>${escapeHtml(poolLabel)} · ID ${input.poolId}</span>
  </div>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) {
    throw new Error('팝업이 차단됨. 브라우저에서 팝업을 허용');
  }
  win.document.write(html);
  win.document.close();
  win.focus();
  win.onload = () => {
    win.print();
  };
}
